import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import {
  type CSSProperties,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Inline scrollbar styles that replicate the custom CSS behavior.
 */
const SCROLLBAR_STYLES: CSSProperties = {
  scrollbarWidth: "thin" as const,
  scrollbarColor: "transparent transparent",
};

/**
 * Default height threshold (px) before the collapsible kicks in.
 * Tuned so ~12–15 lines of body text are visible before clipping.
 */
const MAX_COLLAPSED_HEIGHT = 300;
const OVERFLOW_HYSTERESIS_PX = 4;
const EXPAND_COLLAPSE_DURATION_MS = 300;
const EXPANDED_HEIGHT_BUFFER_PX = 4;

interface CollapsibleMessageContentProps {
  children: ReactNode;
  /** Disable collapsing entirely (e.g. for completion mode or streaming). */
  disabled?: boolean;
  /** Custom max height before collapsing kicks in. */
  maxCollapsedHeight?: number;
  className?: string;
  /**
   * When true, propagate `h-full` to inner wrapper divs so children that use
   * `h-full` can fill the container (Notion-canvas-style snippet editor).
   * Only meaningful when `disabled` is also true.
   */
  fillHeight?: boolean;
}

/**
 * Notion-inspired collapsible wrapper for long playground messages.
 *
 * **Why it doesn't flash:**
 *
 * 1. The DOM tree is always identical — `innerRef` and `scrollRef` are never
 *    conditionally unmounted, so TipTap editors inside never remount.
 *
 * 2. The ResizeObserver only writes a boolean (`isOverflowing`), never a pixel
 *    value that feeds back into a style that could trigger another resize.
 *
 * 3. Expand / collapse animates only from user interaction. Overflow detection
 *    does not write pixel heights back into layout, which avoids threshold loops.
 *
 * **Border handling:**
 *
 * When collapsed the scroll container *becomes* the bordered element
 * (`border border-input rounded-md`) and the ContentArea's own border is
 * suppressed via `[&_.ProseMirror]:!border-0`. The gradient lives inside
 * the scroll container as a `sticky bottom-0` element, so it physically
 * cannot paint over the border. The pill is positioned on a non-overflow
 * wrapper so it can straddle the border with `translate-y-1/2`.
 *
 * When expanded the ContentArea keeps its native border and the pill sits
 * on whatever the last visible border is.
 *
 * **Drag-and-drop safety:**
 *
 * This component is intentionally unaware of drag state. The drag clone
 * produced by @hello-pangea/dnd should preserve exactly the appearance the
 * user sees before dragging (expanded or collapsed). Mutating the visual
 * state during drag causes clipping, missing buttons, and size mismatches
 * with the library's placeholder element.
 */
const CollapsibleMessageContent = ({
  children,
  disabled = false,
  maxCollapsedHeight = MAX_COLLAPSED_HEIGHT,
  className,
  fillHeight = false,
}: CollapsibleMessageContentProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);

  const [isOverflowing, setIsOverflowing] = useState(false);
  const [hasMeasuredOverflow, setHasMeasuredOverflow] = useState(disabled);
  const [isExpanded, setIsExpanded] = useState(false);
  const [expandedMaxHeight, setExpandedMaxHeight] = useState<number | null>(null);
  const prevDisabledRef = useRef(disabled);

  useEffect(() => {
    if (prevDisabledRef.current && !disabled) {
      setIsExpanded(true);
    }
    prevDisabledRef.current = disabled;
  }, [disabled]);

  // ── Stable overflow check — rAF-debounced, boolean-only ─────────────
  useEffect(() => {
    const el = innerRef.current;
    if (disabled) {
      setHasMeasuredOverflow(true);
      return;
    }

    setHasMeasuredOverflow(false);

    if (!el) {
      return;
    }

    let rafId: number;

    const check = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        setIsOverflowing(prev => {
          const threshold = prev
            ? maxCollapsedHeight - OVERFLOW_HYSTERESIS_PX
            : maxCollapsedHeight + OVERFLOW_HYSTERESIS_PX;
          const overflows = el.scrollHeight > threshold;
          setHasMeasuredOverflow(true);
          return prev !== overflows ? overflows : prev;
        });
      });
    };

    const observer = new ResizeObserver(check);
    observer.observe(el);
    check();

    return () => {
      observer.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, [disabled, maxCollapsedHeight]);

  // When content shrinks back under the threshold, reset.
  useEffect(() => {
    if (!isOverflowing) {
      setIsExpanded(false);
      setExpandedMaxHeight(null);
    }
  }, [isOverflowing]);

  const showControls = !disabled && hasMeasuredOverflow && isOverflowing;
  const isPendingOverflowMeasurement = !disabled && !hasMeasuredOverflow;
  const isCollapsed = showControls && !isExpanded;

  const handleToggleMouseDown = useCallback((event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
  }, []);

  const handleToggle = useCallback(() => {
    setIsExpanded(prev => {
      const newState = !prev;
      setExpandedMaxHeight(
        innerRef.current ? innerRef.current.scrollHeight + EXPANDED_HEIGHT_BUFFER_PX : null,
      );
      if (!newState && scrollRef.current) {
        // Scroll to top when collapsing, after animation completes.
        setTimeout(() => {
          scrollRef.current?.scrollTo({ top: 0, behavior: "auto" });
        }, EXPAND_COLLAPSE_DURATION_MS);
      }
      return newState;
    });
  }, []);

  // Compute maxHeight outside JSX to avoid nested ternaries.
  const computedMaxHeight = (() => {
    if (isPendingOverflowMeasurement || isCollapsed) {
      return `${maxCollapsedHeight}px`;
    }
    if (showControls && expandedMaxHeight) {
      return `${expandedMaxHeight}px`;
    }
    return undefined;
  })();

  const shouldConstrainContent = isPendingOverflowMeasurement || isCollapsed;
  const fillHeightClass = fillHeight ? "h-full" : "";

  // ── Render — always the same DOM structure ──────────────────────────
  return (
    <div className={cn("relative", className)}>
      {/*
        Non-overflow wrapper — the pill is positioned relative to this so
        it can protrude past the scroll container's border without being
        clipped by overflow-y-auto.
      */}
      <div className={cn("relative", fillHeightClass)}>
        {/*
          Scrollable viewport.
          When collapsed it takes over as the bordered element so the
          bottom border is always visible. The ContentArea's own border
          is stripped via the descendant selector to avoid doubling.
        */}
        <div
          ref={scrollRef}
          className={cn(
            "overflow-hidden transition-[max-height] duration-300 ease-in-out",
            fillHeightClass,
            shouldConstrainContent && "overflow-y-auto",
            isCollapsed && ["rounded-md border border-input", "[&_.ProseMirror]:!border-0"],
          )}
          style={{
            maxHeight: computedMaxHeight,
            ...(shouldConstrainContent ? SCROLLBAR_STYLES : {}),
          }}
          onMouseEnter={e => {
            if (isCollapsed) {
              (e.currentTarget as HTMLElement).style.scrollbarColor =
                "rgba(0, 0, 0, 0.15) transparent";
            }
          }}
          onMouseLeave={e => {
            if (isCollapsed) {
              (e.currentTarget as HTMLElement).style.scrollbarColor = "transparent transparent";
            }
          }}
        >
          <div ref={innerRef} className={fillHeightClass}>
            {children}
          </div>

          {/*
            Gradient lives INSIDE the scroll container as a sticky element.
            Because it's a child of the bordered element it can never paint
            over the border — it ends at the padding edge.
          */}
          {isCollapsed && (
            <div
              className="pointer-events-none sticky bottom-0 -mt-12 h-12 bg-gradient-to-b from-transparent via-white/80 to-white transition-opacity duration-300"
              aria-hidden="true"
            />
          )}
        </div>

        {/* Toggle pill — straddles the bottom border */}
        {showControls && (
          <Button
            type="button"
            variant="pill"
            size="pill"
            onMouseDown={handleToggleMouseDown}
            onClick={handleToggle}
            className="absolute bottom-0 left-1/2 z-[3] -translate-x-1/2 translate-y-1/2"
            aria-expanded={isExpanded}
            aria-label={isExpanded ? "Collapse message" : "Expand message"}
          >
            {isExpanded ? (
              <ChevronsDownUp className="h-3 w-3" />
            ) : (
              <ChevronsUpDown className="h-3 w-3" />
            )}
            <span className="select-none">{isExpanded ? "Show less" : "Show more"}</span>
          </Button>
        )}
      </div>

      {/* Spacer so the protruding pill doesn't overlap siblings below */}
      {showControls && <div className="h-3" />}
    </div>
  );
};

export default CollapsibleMessageContent;
